import { MenuItem } from '@mui/material';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { isSortedByDirection, isSortSelected, ProjectCategory } from '../../../../../shared/projectsV2';
import * as ProjectsV2Store from '../../../../redux/projectsV2';
import Button from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import { StickyMenu } from '../../../generic/Menu';
import useFetchProjectsAction from '../hooks/useFetchProjectsAction';
import type { ProjectsSort } from '../../../../../shared/projectsV2';

type Category =
  'latest-scan' |
  'last-analyzed';

const SORT_CATEGORIES = {
  'latest-scan': { label: 'Last policy scan' },
  'last-analyzed': { label: 'Revision analyzed' },
};

const IssuesSortingDropdown: VoidFunctionComponent = () => {
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);
  const setSort = useFetchProjectsAction(ProjectsV2Store.actions.setSort);

  const directionIcon = isSortedByDirection(sort, 'desc') ? icons.angleDown : icons.angleUp;

  const [selectedCategory, setSelectedCategory] = useState<Category>('latest-scan');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // Callbacks
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleSort = (category: string) => {
    setSelectedCategory(category as Category);

    setSort(sort === `${category}_desc` ? `${category}_asc` : `${category}_desc`);
    handleClose();
  };

  // Set category - primarily to set the category on mount
  useEffect(() => {
    const categoryKey = sort.split('_').shift() as Category | 'latest-scan';

    // Ensure that we're sorting by a valid issues category
    if (!Object.keys(SORT_CATEGORIES).includes(categoryKey)) {
      // If we're switching to sorting by a different column, change this back to showing last scanned.
      setSelectedCategory('latest-scan');
      return;
    }

    setSelectedCategory(categoryKey);
  }, [sort, setSelectedCategory]);

  return (
    <div>
      <Button
        type="text"
        isFullWidth
        onClick={(e) => handleClick(e)}>
        <div className={classnames('projects-table__header__revision', { 'projects-table__header_active': isSortSelected(sort, 'latest-scan') || isSortSelected(sort, 'last-analyzed') })}>
          <Flex justify="space-between">
            <span className="projects-table__header__revision__label">
              {SORT_CATEGORIES[selectedCategory].label}
            </span>
            {
              (isSortSelected(sort, 'latest-scan') || isSortSelected(sort, 'last-analyzed')) &&
              <Icon icon={directionIcon} size={ICON_SIZE.LG} />
            }
          </Flex>
        </div>
      </Button>
      <StickyMenu
        id="projects-table-revision-sorting-dropdown"
        anchorEl={anchorEl}
        open={isOpen}
        popperClassName="projects-dashboard-v2 projects-table projects-table__header projects-table__header__revision"
        paperClassName="projects-table__header__revision__menu"
        onClose={handleClose}
      >
        <MenuItem
          className="projects-table__header__revision__item"
          onClick={() => handleToggleSort('latest-scan')}
          selected={isSortSelected(sort, 'latest-scan')}
        >
          <Flex justify="space-between" grow={1}>
            <span className="projects-table__header__revision__label">
              Last policy scan
            </span>
            {isSortSelected(sort, 'latest-scan') &&
              <Icon icon={icons.check} />}
          </Flex>
        </MenuItem>
        {/* We can't sort by last analyzed for Release Groups, so only show this option for Projects. */}
        {category === ProjectCategory.Projects && (
          <MenuItem
            className="projects-table__header__revision__item"
            onClick={() => handleToggleSort('last-analyzed')}
            selected={isSortSelected(sort, 'last-analyzed')}
          >
            <Flex justify="space-between" grow={1}>
              <span className="projects-table__header__revision__label">
                Revision analyzed
              </span>
              {isSortSelected(sort, 'last-analyzed') &&
                <Icon icon={icons.check} />}
            </Flex>
          </MenuItem>
        )}
      </StickyMenu>
    </div>
  );
};

export default IssuesSortingDropdown;
